import { format } from 'date-fns'
import type { NewChatEventPayload } from '@renderer/chat/integrations/conversation-action'
import type { ReminderItem } from '@renderer/overlay/Sidepanel/hooks/use-reminders'
import { getSourceLabel } from './get-source-label'
import { isPinnedReminder } from './pinned'

/** What the user asks of a reminder, from the chat button's composer or the Draft action's sent message. */
export function buildReminderDraftPrompt(item: ReminderItem): string {
  return `Turn this reminder into actions and draft each one: ${item.title}`
}

/** What a reminder's attachment chip reads. */
export const REMINDER_ATTACHMENT_LABEL = 'Reminder'

/**
 * The reminder as its attachment reads, to the user in the chip's preview and to the model.
 * `sourceLines` are the transcript lines that raised the reminder, when its meeting kept them.
 */
export function buildReminderMetadataText(item: ReminderItem, sourceLines?: string): string {
  const lines: Array<string> = []
  lines.push(`Title: ${item.title}`)
  lines.push(`Type: ${item.type === 'task' ? 'Task' : 'Note'}`)
  if (isPinnedReminder(item)) lines.push('Pinned: Yes')
  // Absolute, since the chat outlives a "tomorrow".
  lines.push(`Due: ${item.scheduledAt ? format(item.scheduledAt, 'EEE, MMM d, yyyy h:mm a') : 'No due date'}`)
  if (item.source) lines.push(`Source: ${getSourceLabel(item.source)}`)
  if (sourceLines) {
    lines.push('')
    lines.push('What was said in the meeting:')
    lines.push(sourceLines)
  }
  return lines.join('\n')
}

/** A new chat about the reminder, unsent, with the reminder attached and its composer asking for actions. */
export function buildReminderChatPayload(item: ReminderItem, sourceLines?: string): NewChatEventPayload {
  return {
    prompt: buildReminderDraftPrompt(item),
    context: buildReminderMetadataText(item, sourceLines),
    contextLabel: REMINDER_ATTACHMENT_LABEL,
    sourceLabel: 'Reminders',
  }
}
